// state/lesson-entry.ts — Item C2 (2026-06-11) first-visit lesson routing.
//
// The founder-locked decision: "FIRST visit on a sotto-tema = the FULL lesson;
// RETURN = adaptive by mastery". Today even first visits get the distilled
// micro_topic_lesson. The full teaching corpus already exists: the guided lesson
// (argomento_guided_lesson, served by GET /api/lezione/guided/:argomentoId behind
// the `guided_lesson_serve_enabled` flag, rendered by GuidedLessonScreen).
//
// This helper decides, for a lesson CTA, whether to route to the GUIDED full
// lesson (argomento where NOTHING is letta yet) or the per-sotto-tema distilled
// lesson (once anything is letta / guided completed). It is FLAG-GATED: when the
// flag is OFF the helper returns the distilled route verbatim (fail-closed to
// today's behavior).
//
// "Nothing letta yet" for an argomento is read from EXISTING data the callers
// already hold — no new fetch:
//   - ArgomentoDetailScreen: the drill-down micro-topics' `fastState`
//     (every === 'untaught').
//   - Progressi cockpit + Prof home: the studia-grid row's `fastCompleted === 0`.
// The guided-lesson progress store's `completed` flag also forces the distilled
// route (a finished guided lesson must not re-force the full lesson).

import type { StudiaGridArgomentoRow } from '../api/lezione';

/** The persisted distilled Fast lesson route for one micro-topic (today's CTA). */
export function fastLessonRoute(microTopicId: string): string {
  const argomentoId = argomentoIdFromMicroTopicId(microTopicId);
  return argomentoId
    ? guidedLessonRoute(argomentoId, microTopicId)
    : `/studia/lezione/fast/${microTopicId}`;
}

/** The guided full-lesson route for an argomento. */
export function guidedLessonRoute(argomentoId: string, lessonId?: string): string {
  return lessonId
    ? `/studia/lezione/guidata/${argomentoId}/${lessonId}`
    : `/studia/lezione/guidata/${argomentoId}`;
}

export function argomentoIdFromMicroTopicId(microTopicId: string | null | undefined): string | null {
  const match = /^(arg\d+)_mt\d+$/i.exec(microTopicId ?? '');
  return match?.[1] ?? null;
}

export function canonicalLessonRouteForMicroTopic(microTopicId: string): string | null {
  const argomentoId = argomentoIdFromMicroTopicId(microTopicId);
  return argomentoId ? guidedLessonRoute(argomentoId, microTopicId) : null;
}

export interface FirstVisitRouteInput {
  /** Whether the guided-lesson serve flag is ON. OFF → always the distilled route. */
  guidedEnabled: boolean;
  /** True when NOTHING in this argomento is letta yet (first visit). */
  nothingLettaYet: boolean;
  /** True when the student already COMPLETED the guided lesson for this argomento
   *  (its progress.completed). Forces the distilled route so the full lesson is
   *  not re-forced after completion. */
  guidedAlreadyCompleted: boolean;
  /** The argomento the lesson belongs to (target of the guided full lesson). */
  argomentoId: string | null;
  /** The micro-topic the distilled lesson targets (the per-sotto-tema fallback). */
  microTopicId: string | null;
}

/**
 * Resolve the lesson route for a CTA. Returns the GUIDED full-lesson route on a
 * true first visit (flag ON, nothing letta, guided not already completed, a
 * known argomento); otherwise the per-sotto-tema distilled route (or null when
 * neither id is known — the caller falls back to its own default, e.g. /studia).
 */
export function resolveFirstVisitLessonRoute(input: FirstVisitRouteInput): string | null {
  void input.guidedEnabled;
  void input.nothingLettaYet;
  void input.guidedAlreadyCompleted;
  if (input.argomentoId && input.microTopicId) {
    return guidedLessonRoute(input.argomentoId, input.microTopicId);
  }
  if (input.microTopicId) return canonicalLessonRouteForMicroTopic(input.microTopicId);
  if (input.argomentoId) return guidedLessonRoute(input.argomentoId);
  return null;
}

/** True when the studia-grid row for an argomento shows nothing read yet. */
export function gridRowNothingLetta(row: StudiaGridArgomentoRow | undefined | null): boolean {
  if (!row) return false; // unknown → don't force guided (fail to today's behavior)
  return (row.fastCompleted ?? 0) === 0 && !row.deepCompleted;
}
